import { create } from 'zustand';

import {
  getWeatherForProperty,
  refreshWeatherForProperty,
  computeConditionsDisplay,
} from '@/services/weather-service';
import { getCelestialData } from '@/services/celestial-service';
import {
  computeConditionsQuality,
  computeFavorabilityByPeriod,
} from '@/services/conditions-quality-service';
import type { WeatherConditionsDisplay } from '@/services/weather-service';
import type { ConditionsQualityResult } from '@/types/conditions';

interface PropertyCoords {
  lat: number;
  lng: number;
}

interface WeatherState {
  conditions: Record<string, WeatherConditionsDisplay>; // keyed by propertyId
  conditionsQuality: Record<string, ConditionsQualityResult | null>; // keyed by propertyId
  propertyCoords: Record<string, PropertyCoords>; // keyed by propertyId — for 15-min refresh
  loading: Record<string, boolean>;
  errors: Record<string, string | null>;

  fetchConditions: (propertyId: string, lat: number, lng: number) => Promise<void>;
  refreshConditions: (propertyId: string, lat: number, lng: number) => Promise<void>;
  getConditions: (propertyId: string) => WeatherConditionsDisplay | null;
  getConditionsQuality: (propertyId: string) => ConditionsQualityResult | null;
  isLoading: (propertyId: string) => boolean;
  getError: (propertyId: string) => string | null;
  /** Recompute crepuscular proximity for all loaded properties. Called on 15-min interval. */
  _recomputeQuality: () => void;
}

// ---------------------------------------------------------------------------
// Crepuscular refresh interval — recomputes quality every 15 min so the
// time-sensitive crepuscular proximity component stays current between
// weather refreshes (which occur on a 30-min TTL per ADR-018).
// ---------------------------------------------------------------------------

let refreshIntervalId: ReturnType<typeof setInterval> | null = null;

function startRefreshInterval(store: { getState: () => WeatherState }): void {
  if (refreshIntervalId !== null) return;
  refreshIntervalId = setInterval(() => {
    store.getState()._recomputeQuality();
  }, 15 * 60 * 1000);
}

/** Exported for test use: stop the running interval. */
export function _clearQualityRefreshInterval(): void {
  if (refreshIntervalId !== null) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
}

/** Exported for test use: (re)start the interval against the given store ref. */
export function _startQualityRefreshInterval(store: { getState: () => WeatherState }): void {
  _clearQualityRefreshInterval();
  startRefreshInterval(store);
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

function computeQualityForProperty(
  display: WeatherConditionsDisplay | null,
  lat: number,
  lng: number,
  now: Date = new Date(),
): ConditionsQualityResult | null {
  if (!display) return null;
  const celestial = getCelestialData(now, lat, lng);
  return computeConditionsQuality(display, celestial, now);
}

export const useWeatherStore = create<WeatherState>((set, get) => {
  // Lazily start the interval once the store is created. We pass a reference
  // to the store's getState so the interval always calls the latest state.
  const storeRef = { getState: () => get() };
  startRefreshInterval(storeRef);

  return {
    conditions: {},
    conditionsQuality: {},
    propertyCoords: {},
    loading: {},
    errors: {},

    fetchConditions: async (propertyId, lat, lng) => {
      set((s) => ({
        loading: { ...s.loading, [propertyId]: true },
        errors: { ...s.errors, [propertyId]: null },
        propertyCoords: { ...s.propertyCoords, [propertyId]: { lat, lng } },
      }));
      try {
        const data = await getWeatherForProperty(propertyId, lat, lng);
        if (data) {
          const display = computeConditionsDisplay(data);
          const quality = computeQualityForProperty(display, lat, lng);
          // DASH-W1 (Issue 1, path 1): attach the per-day favorability curve from
          // the raw WeatherData in scope. Live-only, additive-optional (ADR-019).
          if (quality) {
            quality.favorabilityByPeriod = computeFavorabilityByPeriod(
              data,
              new Date(),
              lat,
              lng,
            );
          }
          set((s) => ({
            conditions: { ...s.conditions, [propertyId]: display },
            conditionsQuality: { ...s.conditionsQuality, [propertyId]: quality },
            loading: { ...s.loading, [propertyId]: false },
          }));
        } else {
          set((s) => ({
            conditionsQuality: { ...s.conditionsQuality, [propertyId]: null },
            loading: { ...s.loading, [propertyId]: false },
            errors: { ...s.errors, [propertyId]: 'Weather unavailable — no connection' },
          }));
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load weather';
        console.error('[weather-store] fetchConditions:', err);
        set((s) => ({
          conditionsQuality: { ...s.conditionsQuality, [propertyId]: null },
          loading: { ...s.loading, [propertyId]: false },
          errors: { ...s.errors, [propertyId]: message },
        }));
      }
    },

    refreshConditions: async (propertyId, lat, lng) => {
      set((s) => ({
        loading: { ...s.loading, [propertyId]: true },
        errors: { ...s.errors, [propertyId]: null },
        propertyCoords: { ...s.propertyCoords, [propertyId]: { lat, lng } },
      }));
      try {
        const data = await refreshWeatherForProperty(propertyId, lat, lng);
        if (data) {
          const display = computeConditionsDisplay(data);
          const quality = computeQualityForProperty(display, lat, lng);
          // DASH-W1 (Issue 1, path 2): attach the per-day favorability curve from
          // the raw WeatherData in scope. Live-only, additive-optional (ADR-019).
          if (quality) {
            quality.favorabilityByPeriod = computeFavorabilityByPeriod(
              data,
              new Date(),
              lat,
              lng,
            );
          }
          set((s) => ({
            conditions: { ...s.conditions, [propertyId]: display },
            conditionsQuality: { ...s.conditionsQuality, [propertyId]: quality },
            loading: { ...s.loading, [propertyId]: false },
          }));
        } else {
          set((s) => ({
            conditionsQuality: { ...s.conditionsQuality, [propertyId]: null },
            loading: { ...s.loading, [propertyId]: false },
            errors: { ...s.errors, [propertyId]: 'Weather unavailable — no connection' },
          }));
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to refresh weather';
        console.error('[weather-store] refreshConditions:', err);
        set((s) => ({
          conditionsQuality: { ...s.conditionsQuality, [propertyId]: null },
          loading: { ...s.loading, [propertyId]: false },
          errors: { ...s.errors, [propertyId]: message },
        }));
      }
    },

    getConditions: (propertyId) => get().conditions[propertyId] ?? null,

    getConditionsQuality: (propertyId) => get().conditionsQuality[propertyId] ?? null,

    isLoading: (propertyId) => get().loading[propertyId] ?? false,

    getError: (propertyId) => get().errors[propertyId] ?? null,

    _recomputeQuality: () => {
      const { conditions, propertyCoords, conditionsQuality } = get();
      const now = new Date();
      const updates: Record<string, ConditionsQualityResult | null> = {};

      for (const propertyId of Object.keys(conditions)) {
        const coords = propertyCoords[propertyId];
        if (!coords) continue;
        // DASH-W1 (Issue 1, path 3 / option b): the recompute cannot derive the
        // per-day favorability curve (no raw WeatherData in scope at tick time),
        // so carry the prior curve forward onto the freshly-recomputed result.
        // The per-day curve changes only across the calendar day; only the
        // crepuscular "now" proximity advances on the 15-min tick. Without this
        // the tick would silently strip the curve and the trend panel would blink
        // to empty.
        const prevCurve = conditionsQuality[propertyId]?.favorabilityByPeriod;
        const recomputed = computeQualityForProperty(
          conditions[propertyId],
          coords.lat,
          coords.lng,
          now,
        );
        if (recomputed) {
          recomputed.favorabilityByPeriod = prevCurve;
        }
        updates[propertyId] = recomputed;
      }

      set((s) => ({
        conditionsQuality: { ...s.conditionsQuality, ...updates },
      }));
    },
  };
});
